/** Narrow an unknown caught value to a display string (Error.message, else String()).
 *  Shared by the try/catch toast sites so the coercion lives in one place. */
export function errMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export type GlobalToastVariant = 'error' | 'info';

export interface GlobalToast {
  id: number;
  title: string;
  /** Body under the title; omitted when the title alone says it. */
  message?: string;
  variant: GlobalToastVariant;
  /** Optional one-click follow-up (e.g. "View in tree"). Clicking it dismisses the toast. */
  action?: { label: string; onClick: () => void };
}

type ToastListener = () => void;

let currentToast: GlobalToast | null = null;
let nextToastId = 1;
const listeners = new Set<ToastListener>();

function notifyToastListeners() {
  for (const listener of Array.from(listeners)) {
    listener();
  }
}

function showGlobalToast(toast: Omit<GlobalToast, 'id' | 'variant'>, variant: GlobalToastVariant) {
  currentToast = { ...toast, variant, id: nextToastId++ };
  notifyToastListeners();
}

export function showGlobalErrorToast(toast: Omit<GlobalToast, 'id' | 'variant'>) {
  showGlobalToast(toast, 'error');
}

/** Non-error advisory (e.g. an inert engine-only solve param was stripped on load). */
export function showGlobalInfoToast(toast: Omit<GlobalToast, 'id' | 'variant'>) {
  showGlobalToast(toast, 'info');
}

export function clearGlobalToast(id?: number) {
  if (id !== undefined && currentToast?.id !== id) return;
  currentToast = null;
  notifyToastListeners();
}

export function getGlobalToastSnapshot() {
  return currentToast;
}

export function subscribeGlobalToast(listener: ToastListener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
